import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { AccountService } from 'src/app/core/services/account.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { selectAuthEmail } from 'src/app/state/auth.selectors';
import { BusinessAccountWithChildrenDto, UserAccountHomeDto, MarketingAdDto, UserAccountRegDto, CountryDto, BusinessAccountTypeNameMapping } from 'src/app/core/dtos/dto';
import { loadMyAccount } from 'src/app/state/my-account.actions';
import { selectBusinessAccountTypeNameMapping, selectMyAccount } from 'src/app/state/my-account.selectors';
import { BusinessAccountService } from 'src/app/core/services/business-account.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { CountryRegionService } from 'src/app/core/services/country-region.service';
import { BrandEnum } from 'src/app/core/dtos/enum';
import { take } from 'rxjs/operators';
import { loadShoppingCart } from 'src/app/state/shopping-cart.actions';

@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss']
})
export class MyAccountComponent implements OnInit, OnDestroy {

  @ViewChild('resendConfirmEmail') resendConfirmEmail: ModalComponent;

  myAccount$: Observable<UserAccountHomeDto>;
  userEmail$: Observable<string>;
  accountSubscription: Subscription;
  marketingAdSubscription: Subscription;
  countriesSubscription: Subscription;
  businessAccountSubscription: Subscription;
  confirmEmailSubscription: Subscription;
  shoppingCartSubscription: Subscription;
  destroy$: Subject<boolean> = new Subject<boolean>();
  brandId: BrandEnum;
  marketingAd: MarketingAdDto;
  marketingAdImage: string;
  regDropDownOptions = {};
  registrations: UserAccountRegDto[];
  myAccountResult: UserAccountHomeDto;
  launchSRDownload: boolean;
  launchScoringTimelineSwitch: number;
  regPoliciesUrl: string;
  testDayInfoUrl: string;
  modalBody: string;
  modalTitle: string;
  businessAccount: BusinessAccountWithChildrenDto;
  countries: CountryDto[];
  userForm: FormGroup;
  showBusinessResource = false;
  businessAccountTypeNameMapping$: Observable<BusinessAccountTypeNameMapping>;

  constructor(
    private accountService: AccountService,
    private store: Store,
    private formBuilder: FormBuilder,
    private toastService: HotToastService,
    private countryRegionService: CountryRegionService,
    private configHelper: ConfigHelper,
    private shoppingCartService: ShoppingCartService,
    private businessAccountService: BusinessAccountService
  ) { }

  ngOnInit(): void {
    this.brandId = this.configHelper.getBrandId();
    this.myAccount$ = this.store.pipe(select(selectMyAccount));
    this.userEmail$ = this.store.pipe(select(selectAuthEmail));
    this.launchSRDownload = this.configHelper.getLaunchSRDownload() === 1;
    this.regPoliciesUrl = this.configHelper.getRegPoliciesUrl();
    this.testDayInfoUrl = this.configHelper.getTestDayInfoUrl();
    this.getMyAccount();
    this.getMarketingAd();
    this.loadShoppingCart();
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  getMyAccount() {
    this.accountSubscription = this.accountService.getMyAccountInfo()
      .subscribe(
        result => {
          this.store.dispatch(loadMyAccount({ myAccount: result.content }));
          this.myAccountResult = result.content;
          this.registrations = this.myAccountResult.registrations;
          if (this.myAccountResult.linkedToActiveBusinessAccount) {
            this.getBusinessAccount();
            if (this.brandId === BrandEnum.CELPIP) {
              this.createForm();
              this.getCountries();
            }
          }
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }

  createForm() {
    this.userForm = this.formBuilder.group({
      companyName: [''],
      industry: [''],
      contactFirstName: [''],
      contactLastName: [''],
      salutation: [''],
      email: [''],
      phone: [''],
      countryId: [''],
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      postalCode: [''],
      region: [''],
    });
  }

  getMarketingAd() {
    this.marketingAdSubscription = this.accountService.getMarketingAd()
      .subscribe(
        result => {
          if (result) {
            this.marketingAd = result.content;
            this.marketingAdImage = this.accountService.getMarketingAdImageSrc(this.marketingAd.id);
          }
        }
      );
  }

  resendConfirmationEmail(reg: UserAccountRegDto) {
    this.store.select(selectAuthEmail).pipe(take(1))
      .subscribe(email => {
        this.confirmEmailSubscription = this.accountService.sendTestConfirmationEmail(reg.regId)
          .subscribe(
            () => {
              this.modalTitle = "success";
              this.modalBody = "Your Test Confirmation Email has been resent to " + email + ". If you do not see the Test Confirmation Email in your inbox, please make sure to check your junk and spam folders.";
              this.resendConfirmEmail.open();
            },
            (error: HttpErrorResponse) => {
              this.modalTitle = "error";
              this.modalBody = "An error occurred when sending your Test Confirmation Email. \n";
              if (error.status === 400) {
                const errorMessage = error.error as HttpErrorResponseMessages;
                errorMessage.reasons.forEach(reasons => {
                  this.modalBody += reasons.errorMessage;
                });
              }
              this.resendConfirmEmail.open();
            }
          );
      });

  }

  getCountries() {
    this.countriesSubscription = this.countryRegionService.getCountries()
      .subscribe(result => {
        this.countries = result.content;
      },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            this.toastService.warning("Unable to load countries.");
          }
        });
  }

  onNavigateParentClick() {
    this.businessAccountService.navigateToParentAccountFromChild();
  }

  loadShoppingCart() {
    this.shoppingCartSubscription = this.shoppingCartService.getShoppingCart()
      .subscribe(
        res => {
          this.store.dispatch(loadShoppingCart({ shoppingCart: res.content }));
        },
        () => {
          this.toastService.warning("Unable to load shopping cart.");
        });
  }

  getBusinessAccount() {
    this.businessAccountSubscription = this.accountService.getBusinessAccount()
      .subscribe(result => {
        this.getShowBusinessResource();
        this.businessAccount = result.content;
        this.userForm?.patchValue(this.businessAccount.info);
      },
        () => {
          this.toastService.warning("Unable to load account.");
        });
  }
  getShowBusinessResource() {
    this.businessAccountTypeNameMapping$ = this.store.select(selectBusinessAccountTypeNameMapping).pipe(take(1));
    this.businessAccountTypeNameMapping$
      .subscribe(result => {
        if (result.resourcesTypeName === "International Agents" || result.resourcesTypeName === "International Preparation Providers") {
          this.showBusinessResource = true;
        }

      });
  }

  ngOnDestroy() {
    this.accountSubscription?.unsubscribe();
    this.marketingAdSubscription?.unsubscribe();
    this.businessAccountSubscription?.unsubscribe();
    this.countriesSubscription?.unsubscribe();
    this.confirmEmailSubscription?.unsubscribe();
    this.shoppingCartSubscription?.unsubscribe();
  }
}
